<template>
  <div :style="styleColorBar">
    <canvas
      style="margin: 0px"
      :width="canvasW"
      :height="canvasH"
      @mousedown="mousedown"
      ref="canvasColorBar"
    />
    <div class="div-label">
      <div class="div-step">{{ shownStep }}</div>
      <div class="div-absrel">{{ shownAbsRel }}</div>
    </div>
    <color-bar-prop-settings
      v-if="showSettings"
      @save="saveColorBarSettings"
      @close="closeColorBarSettings"
      :show.sync="showSettings"
      :propSettings="settings"
      :currentType="colorBarType"
      :mapIndexUI="mapIndexUI"
      :shownType="shownType"
      :parentForm="parentForm"
      :indexInForm="indexInForm"
    />
  </div>
</template>

<script>
import ColorMapHelper from "../js/ColorMapHelper";
import ColorBarPropSettings from "./ColorBarPropSettings";
import { TOPOGRAPHYEVENTS } from "../shared/events";
import fileSys from "../../fileoprators/fileSystem";
import { TOPOGRAPHYMODES } from "../../shared/consts";
import { logStdout } from "../../shared/utils";
import { MAPSTYLES } from "../shared";

const fs = window.require("fs");
const DEFAULT_COLORBAR_CURV = { bar: "AbsNormal", style: "standard", step: 0, num: 61, unit: "D" };
const DEFAULT_COLORBAR_ELEV = { bar: "AbsNormal", style: "standard", step: 0, num: 61, unit: "μm" };
const DEFAULT_COLORBAR_HEIGHTF = {
  bar: "AbsNormal",
  style: "standard",
  step: 0.05,
  num: 61,
  unit: "mm"
};
const DEFAULT_COLORBAR_TFSQ = {
  bar: "AbsNormal",
  style: "standard",
  step: 0.017,
  num: 61,
  unit: "μm"
};

export default {
  name: "ColorBar",
  components: {
    ColorBarPropSettings
  },
  props: {
    dispW: { type: Number, default: 118 },
    dispH: { type: Number, default: window.screen.height - 80 - 10 },
    isDiff: { type: Boolean, default: false },
    parentForm: { type: String, default: "" },
    indexInForm: { type: Number, default: 0 }
  },
  data: function () {
    return {
      settings: null,
      shownType: "normal",
      colorNum: 61,
      csArray: [],
      siArray: [],
      mapIndexUI: 1,
      shownStep: "D",
      shownAbsRel: "",
      hadDrawnMapName: [],
      showSettings: false,
      colorBarType: "curvature",
      cfgPath: "",
      busLabel: "--"
    };
  },
  created() {
    if (this.parentForm === TOPOGRAPHYMODES.contectlens.key) {
      this.colorBarType = "rgp";
    }
    this.initProp();
    this.regEvents();
  },
  destroyed() {
    this.$bus.$off(TOPOGRAPHYEVENTS.mapStyleChanged + this.busLabel);
    this.$bus.$off(TOPOGRAPHYEVENTS.refreshScaleColor + this.busLabel);
    this.$bus.$off(TOPOGRAPHYEVENTS.refreshFocus + this.busLabel);
    this.$bus.$off(TOPOGRAPHYEVENTS.zernikeColorBarChanged + this.busLabel);
    if (this.parentForm === "compare2records") {
      this.$bus.$off(TOPOGRAPHYEVENTS.diffColorBarChanged);
    }
  },
  methods: {
    regEvents() {
      this.$bus.$on(TOPOGRAPHYEVENTS.mapStyleChanged + this.busLabel, (mapIndexUI) => {
        this.onMapStyleChanged(mapIndexUI);
      });
      this.$bus.$on(TOPOGRAPHYEVENTS.refreshScaleColor + this.busLabel, (csArray, siArray) => {
        this.onRefreshScaleColor(csArray, siArray);
      });
      this.$bus.$on(TOPOGRAPHYEVENTS.refreshFocus + this.busLabel, (name, index, draw = true) => {
        this.onRefreshFocus(name, index, draw);
      });
      this.$bus.$on(TOPOGRAPHYEVENTS.zernikeColorBarChanged + this.busLabel, (cntVal, step) => {
        this.settings.elevation.cntVal = cntVal;
        this.settings.elevation.step = step;
        this.refreshColorBarPropShown();
        this.$bus.$emit(
          TOPOGRAPHYEVENTS.colorBarPropChanged + this.busLabel,
          this.settings.elevation
        );
      });
      if (this.parentForm === "compare2records") {
        this.$bus.$on(TOPOGRAPHYEVENTS.diffColorBarChanged, (colorProp, isDiff) => {
          if (this.isDiff !== isDiff) {
            let tmp = this.settings[this.colorBarType];
            tmp.num = colorProp.num;
            tmp.style = colorProp.style;
            tmp.unit = colorProp.unit;
            var colorBarSettings = JSON.parse(fs.readFileSync(this.cfgPath, "utf-8"));
            colorBarSettings[this.shownType] = this.settings;
            fs.writeFileSync(this.cfgPath, JSON.stringify(colorBarSettings));
            this.$bus.$emit(
              TOPOGRAPHYEVENTS.colorBarPropChanged + this.busLabel,
              this.settings[this.colorBarType],
              false
            );
          }
        });
      }
    },
    onMapStyleChanged(mapIndexUI) {
      this.mapIndexUI = mapIndexUI;

      this.getColorBarSettings();
      this.refreshColorBarPropShown();

      this.$bus.$emit(
        TOPOGRAPHYEVENTS.colorBarPropChanged + this.busLabel,
        this.settings[this.colorBarType]
      );
    },
    onRefreshScaleColor(csArray, siArray) {
      this.csArray = csArray;
      this.siArray = siArray;
      this.drawColorBar();
    },
    onRefreshFocus(name, index, draw) {
      if (this.hadDrawnMapName.indexOf(name) >= 0) {
        this.hadDrawnMapName = [];
        this.clearFocus();
      }
      if (draw && draw === true) {
        this.drawFocus(index);
        this.hadDrawnMapName.push(name);
      }
    },
    initProp() {
      if (this.parentForm === "compare2records" && !this.isDiff) {
        this.busLabel = this.parentForm + "0";
      } else {
        this.busLabel = this.parentForm + this.indexInForm;
      }
      if (this.parentForm === "compare2records") {
        this.shownType = this.indexInForm === 0 ? "compare" : "compareDiff";
      } else {
        this.shownType = this.busLabel;
      }
      this.cfgPath = fileSys.getTopographyCfgPath("colorBarShown.json");

      this.getColorBarSettings();
      if (this.parentForm === "zernike") {
        this.settings.elevation = {
          bar: "Rel",
          num: 61,
          step: 2.5,
          style: "standard",
          unit: "μm",
          cntVal: -1
        };
      } else if (
        this.parentForm === "fourierAnalysis" &&
        (this.indexInForm === 1 || this.indexInForm === 3 || this.indexInForm === 4)
      ) {
        this.setFourierColorBar(this.settings.curvature);
      }
    },
    getColorBarSettings() {
      var colorBarSettings = JSON.parse(fs.readFileSync(this.cfgPath, "utf-8"));
      if (colorBarSettings && colorBarSettings[this.shownType]) {
        this.settings = colorBarSettings[this.shownType];
      } else {
        this.settings = {};
      }
      this.settings.curvature =
        this.settings.curvature || JSON.parse(JSON.stringify(DEFAULT_COLORBAR_CURV));
      this.settings.elevation =
        this.settings.elevation || JSON.parse(JSON.stringify(DEFAULT_COLORBAR_ELEV));
      this.settings.heightf =
        this.settings.heightf || JSON.parse(JSON.stringify(DEFAULT_COLORBAR_HEIGHTF));
      this.settings.tfsq = this.settings.tfsq || JSON.parse(JSON.stringify(DEFAULT_COLORBAR_TFSQ));

      if (
        this.parentForm === "fourierAnalysis" &&
        (this.indexInForm === 1 || this.indexInForm === 3 || this.indexInForm === 4)
      ) {
        this.setFourierColorBar(this.settings.curvature);
      }
      colorBarSettings[this.shownType] = this.settings;
      logStdout("colorbar settings changed", this.shownType, this.settings);
      fs.writeFileSync(this.cfgPath, JSON.stringify(colorBarSettings));
    },
    saveColorBarSettings() {
      var colorBarSettings = JSON.parse(fs.readFileSync(this.cfgPath, "utf-8"));
      if (colorBarSettings && colorBarSettings[this.shownType]) {
        if (
          this.parentForm === "fourierAnalysis" &&
          (this.indexInForm === 1 || this.indexInForm === 3 || this.indexInForm === 4)
        ) {
          this.setFourierColorBar(this.settings.curvature);
        }

        if (this.mapIndexUI === MAPSTYLES["FRP"]) {
          this.settings.curvature.unit = colorBarSettings[this.shownType].curvature.unit;
        }
        colorBarSettings[this.shownType] = this.settings;

        fs.writeFileSync(this.cfgPath, JSON.stringify(colorBarSettings));
        this.$bus.$emit(
          TOPOGRAPHYEVENTS.colorBarPropChanged + this.busLabel,
          this.settings[this.colorBarType],
          true
        );
      }
    },
    setFourierColorBar(objCurvature) {
      objCurvature.cntVal = 0;
      objCurvature.bar = "RelMedium";
      objCurvature.step = 0.5;
      objCurvature.unit = "D";
    },
    closeColorBarSettings() {
      var colorBarSettings = JSON.parse(fs.readFileSync(this.cfgPath, "utf-8"));
      if (colorBarSettings && colorBarSettings[this.shownType]) {
        this.settings = colorBarSettings[this.shownType];
      }
    },
    mousedown(event) {
      if (!event || !event.buttons || this.parentForm === "zernike") return;

      if (event.buttons === 1 || event.buttons === 2) {
        this.getColorBarSettings();
        this.showSettings = true;
      }
    },
    getContext() {
      let canvas = this.$refs.canvasColorBar;
      if (!canvas || !this.csArray || !this.siArray) return null;
      let context = canvas.getContext("2d");
      return context;
    },
    refreshColorBarPropShown() {
      let result = ColorMapHelper.getColorBarShownInfo(this.mapIndexUI, this.settings);
      this.colorBarType = result.colorBarType;
      this.shownStep = result.step;
      this.shownAbsRel = result.absOrRel;
    },
    drawColorBar() {
      this.refreshColorBarPropShown();
      if (!this.settings || !this.colorBarType || !this.settings[this.colorBarType]) {
        return;
      }
      this.colorNum = this.settings[this.colorBarType].num;
      ColorMapHelper.drawColorBar(
        this.getContext(),
        this.colorNum,
        this.canvasW,
        this.canvasH,
        this.csArray,
        this.siArray,
        this.parentForm === TOPOGRAPHYMODES.contectlens.key
      );
    },
    drawFocus(index) {
      ColorMapHelper.drawFocus(
        this.getContext(),
        index,
        this.colorNum,
        this.canvasW,
        this.canvasH,
        this.csArray
      );
    },
    clearFocus() {
      this.drawColorBar();
    }
  },
  computed: {
    styleColorBar: function () {
      let style = {};
      style.width = `${this.dispW - 2}px`;
      style.height = `${this.dispH - 2}px`;
      style.padding = "0px";
      style.backgroundColor = "#2D3039";
      style.border = "1px solid grey";
      return style;
    },
    canvasW: function () {
      return this.dispW - 2;
    },
    canvasH: function () {
      return this.dispH - 55;
    }
  }
};
</script>

<style lang="less" scoped>
.div-label {
  padding: 0px;
  width: 100%;
  height: 50px;
  font-size: 14px;
  text-align: center;
}
.div-step {
  margin: 0px;
  height: 25px;
  line-height: 25px;
  border-top: 1px solid grey;
  border-bottom: 1px solid grey;
  color: white;
}
.div-absrel {
  margin: 0px;
  height: 25px;
  line-height: 25px;
  color: white;
}
</style>



// WEBPACK FOOTER //
// src/topography/components/ColorBar.vue